import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, categories, comments } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-20 px-4 text-center">
          <h1 className="text-2xl font-bold text-foreground-950">文章未找到</h1>
          <Link to="/" className="mt-4 inline-block text-primary-600 hover:underline">返回首页</Link>
        </main>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.slug === article.category);
  const articleComments = comments.filter((c) => c.articleId === article.id);
  const relatedArticles = articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 4);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.51chigua.com/' },
      ...(category ? [{ '@type': 'ListItem' as const, position: 2, name: category.name, item: `https://www.51chigua.com/category/${category.slug}` }] : []),
      { '@type': 'ListItem', position: category ? 3 : 2, name: article.title, item: `https://www.51chigua.com/article/${article.id}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '五一吃瓜网',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.51chigua.com/article/${article.id}`,
    },
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Navbar />

      <main>
        <article className="pt-24 md:pt-32">
          <header className="px-4 md:px-6 max-w-4xl mx-auto text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-foreground-950 transition-colors whitespace-nowrap">首页</Link>
              <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
              {category && (
                <>
                  <Link to={`/category/${category.slug}`} className="hover:text-foreground-950 transition-colors whitespace-nowrap">{category.name}</Link>
                  <span className="w-3 h-3 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
                </>
              )}
              <span className="text-foreground-950 font-medium line-clamp-1">{article.title}</span>
            </nav>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-black text-foreground-950 leading-tight">
              {article.title}
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {article.summary}
            </p>

            <div className="flex items-center justify-center gap-4 mt-6 text-xs text-foreground-500 flex-wrap">
              {category && (
                <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-full font-medium whitespace-nowrap">
                  {category.name}
                </span>
              )}
              <span>{article.author}</span>
              <span>{article.publishDate}</span>
              <span className="flex items-center gap-1">
                <span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-eye-line"></i></span>
                {article.readCount >= 10000 ? `${(article.readCount / 10000).toFixed(1)}万` : article.readCount}
              </span>
            </div>

            <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          <div className="mt-10 px-4 md:px-6 max-w-5xl mx-auto">
            <div className="relative rounded-3xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`五一吃瓜网 - ${article.title}`}
                className="w-full h-60 md:h-80 lg:h-96 object-cover object-top rounded-3xl"
              />
            </div>
          </div>

          <section className="py-14 md:py-20 px-4 md:px-6 max-w-3xl mx-auto">
            <div className="flex justify-center mb-10">
              <span className="px-4 py-1.5 border border-foreground-300 rounded-full text-xs font-medium text-foreground-600 whitespace-nowrap">
                深度报道
              </span>
            </div>

            <div
              className="prose-custom text-foreground-800 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />
          </section>
        </article>

        {articleComments.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6">
            <div className="max-w-4xl mx-auto bg-foreground-950 rounded-3xl p-8 md:p-12 relative overflow-hidden">
              <div className="relative z-10">
                <span className="text-6xl md:text-7xl font-serif text-accent-100/40 leading-none block">&ldquo;</span>
                <div className="space-y-8 mt-4">
                  {articleComments.map((comment) => (
                    <div key={comment.id} className="flex gap-4 items-start">
                      <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0">
                        <img src={comment.avatar} alt={comment.user} className="w-full h-full object-cover" loading="lazy" />
                      </div>
                      <div className="flex-1">
                        <p className="text-background-50 text-sm md:text-base leading-relaxed">{comment.content}</p>
                        <div className="flex items-center gap-3 mt-3 text-xs text-foreground-400">
                          <span className="font-medium text-background-50/80">{comment.user}</span>
                          <span>{comment.date}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-10">
                  <button className="px-8 py-2.5 bg-accent-100 text-foreground-950 rounded-full text-sm font-semibold hover:bg-accent-200 transition-all duration-300 whitespace-nowrap cursor-pointer">
                    写评论
                  </button>
                </div>
              </div>

              <div className="absolute bottom-0 right-0 opacity-5 pointer-events-none select-none">
                <span className="text-[180px] font-heading font-black leading-none">吃瓜</span>
              </div>
            </div>
          </section>
        )}

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 px-4 md:px-6 max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-10 md:mb-14">
              猜你想吃
            </h2>

            <div className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4">
              {relatedArticles.map((related) => (
                <Link
                  key={related.id}
                  to={`/article/${related.id}`}
                  className="group block bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-background-300/80 transition-all duration-300 flex-shrink-0 w-[280px] md:w-[300px]"
                >
                  <div className="relative h-44 overflow-hidden">
                    <img src={related.image} alt={related.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    <span className="absolute top-3 left-3 px-2.5 py-1 bg-foreground-950 text-background-50 text-xs font-medium rounded-lg whitespace-nowrap">
                      相关
                    </span>
                  </div>
                  <div className="p-4">
                    <h3 className="text-base font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">{related.title}</h3>
                    <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                      <span>{related.publishDate}</span>
                      <span className="flex items-center gap-1"><span className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-eye-line"></i></span>{related.readCount >= 10000 ? `${(related.readCount / 10000).toFixed(1)}万` : related.readCount}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}